// ─────────────────────────────────────────────────────────────
// modal-utils.jsx — Shared modal positioning hooks + shell-styles + headers.
// Extrahiert aus app.jsx damit widgets.jsx und flea-Module
// diese Utilities importieren können ohne zirkuläre Abhängigkeiten.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { Z } from './theme.jsx';

// ─────────────────────────────────────────────────────────────
// useModalViewport — tracked visualViewport für Keyboard-Awareness.
//   • kbOpen=false → Modal vertikal zentriert
//   • kbOpen=true  → Modal folgt dem Visual-Viewport (iOS-safe)
// ─────────────────────────────────────────────────────────────
export function useModalViewport() {
  const [vp, setVp] = useState(() => {
    if (typeof window === "undefined") return { height: 800, kbOpen: false, offsetTop: 0 };
    const vv = window.visualViewport;
    return { height: vv?.height ?? window.innerHeight, kbOpen: false, offsetTop: vv?.offsetTop || 0 };
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) {
      const onResize = () => setVp({ height: window.innerHeight, kbOpen: false, offsetTop: 0 });
      window.addEventListener("resize", onResize);
      onResize();
      return () => window.removeEventListener("resize", onResize);
    }
    const update = () => {
      const vvH = vv.height;
      const winH = window.innerHeight;
      const kbOpen = (winH - vvH) > 80;
      setVp({ height: vvH, kbOpen, offsetTop: vv.offsetTop || 0 });
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return vp;
}

// modalShellStyle — Positions-/Scroll-Styles für zentrierte Overlay-Modals.
export function modalShellStyle({ vh, kbOpen, offsetTop = 0, glass, F, dark = false, width = 400, radius = 24, padding = "20px 22px 22px", zIndex = Z.sheet }) {
  const glassBg = dark
    ? "#2C2C2E"
    : "rgba(250, 250, 252, 0.82)";
  return {
    position: "fixed", left: "50%",
    top: `${offsetTop + vh / 2}px`,
    transform: "translate3d(-50%, -50%, 0)",
    width: `min(${width}px, calc(100vw - 64px))`,
    maxHeight: `${Math.max(200, vh - 80)}px`,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    background: glassBg,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
    boxShadow: dark
      ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
      : "0 24px 60px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
    borderRadius: radius,
    zIndex,
    animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
    willChange: "opacity",
    padding, fontFamily: F,
  };
}

// bottomSheetShellStyle — Liquid-Glass Bottom-Sheet Styles.
export function bottomSheetShellStyle({ vh, kbOpen, offsetTop = 0, F, dark = false, width = 560, padding = "0 22px 22px", zIndex = Z.sheet }) {
  const winH = (typeof window !== "undefined" ? window.innerHeight : 800);
  const bottomOffset = Math.max(0, winH - (offsetTop + vh));
  const glassBg = dark ? "#2C2C2E" : "rgba(250, 250, 252, 0.88)";
  const TOP_RESERVE = 80;
  return {
    position: "fixed", left: "50%",
    bottom: `${bottomOffset}px`,
    transform: "translateX(-50%)",
    width: `min(${width}px, calc(100vw - 16px))`,
    maxHeight: `${Math.max(280, vh - TOP_RESERVE)}px`,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    background: glassBg,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
    borderBottom: "none",
    boxShadow: dark
      ? "0 -12px 40px rgba(0,0,0,0.5), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
      : "0 -12px 40px rgba(0,0,0,0.14), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
    borderRadius: "28px 28px 0 0",
    zIndex,
    animation: "slideUp 0.34s cubic-bezier(0.32, 0.72, 0, 1) both",
    willChange: "transform, opacity",
    padding, fontFamily: F,
    paddingBottom: `calc(env(safe-area-inset-bottom, 12px) + 12px)`,
  };
}

// ModalCloseButton — universeller iOS-26-style Schließ-Button (xmark.circle.fill).
export function ModalCloseButton({ onClose, dark, variant = "auto" }) {
  const onDark = variant === "onDark" || (variant === "auto" && dark);
  const circleFill = onDark ? "rgba(120,120,128,0.36)" : "rgba(118,118,128,0.18)";
  const xStroke    = onDark ? "rgba(235,235,245,0.65)" : "rgba(60,60,67,0.6)";
  return (
    <button
      onClick={onClose}
      aria-label="Schließen"
      onMouseDown={e => e.currentTarget.style.transform = "scale(0.92)"}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      onTouchStart={e => e.currentTarget.style.transform = "scale(0.92)"}
      onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
      style={{
        background: "transparent", border: "none", padding: 0,
        width: 30, height: 30, cursor: "pointer", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "transform 0.14s cubic-bezier(0.32,0.72,0,1)",
      }}
    >
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="15" fill={circleFill}/>
        <path d="M10.5 10.5 L19.5 19.5 M19.5 10.5 L10.5 19.5"
          stroke={xStroke} strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    </button>
  );
}

// BottomSheetHeader — Drag-Indicator + Titel + optionale Subtitle + Close.
export function BottomSheetHeader({ title, subtitle, t, dark, F, onClose, marginBottom = 16 }) {
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 2, paddingTop: 8, paddingBottom: marginBottom, marginLeft: -22, marginRight: -22, paddingLeft: 22, paddingRight: 22, background: "transparent" }}>
      <div style={{
        width: 40, height: 4, borderRadius: 2,
        background: dark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)",
        margin: "0 auto 12px",
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", minHeight: 30 }}>
        <span style={{
          flex: 1, textAlign: "center",
          fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px",
          fontFamily: F,
          padding: onClose ? "0 38px 0 38px" : "0 8px",
        }}>{title}</span>
        {onClose && (
          <div style={{ position: "absolute", right: 0, top: 0 }}>
            <ModalCloseButton onClose={onClose} dark={dark} />
          </div>
        )}
      </div>
      {subtitle && (
        <div style={{ fontSize: 13, color: t.textSub, textAlign: "center", marginTop: 6, lineHeight: 1.45, fontFamily: F, padding: "0 8px" }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ModalHeader — einheitlicher Header für Confirmation-/Edit-Modals.
export function ModalHeader({ title, subtitle, t, dark, F, onClose, accent, accentIcon, marginBottom = 20 }) {
  const closeBtn = <ModalCloseButton onClose={onClose} dark={dark} />;

  if (accent && accentIcon) {
    return (
      <div style={{ marginBottom, position: "relative", display: "flex", alignItems: "center", gap: 12, paddingRight: 38 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: accent.weak,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent.solid,
        }}>{accentIcon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", fontFamily: F, lineHeight: 1.2 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: t.textSub, marginTop: 3, fontFamily: F, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{ position: "absolute", top: 0, right: 0 }}>{closeBtn}</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: subtitle ? 6 : marginBottom, position: "relative", minHeight: 30 }}>
        <span style={{
          position: "absolute", left: 0, right: 0, textAlign: "center",
          fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px",
          pointerEvents: "none", fontFamily: F,
          padding: "0 46px",
        }}>{title}</span>
        {closeBtn}
      </div>
      {subtitle && (
        <div style={{ fontSize: 13, color: t.textSub, textAlign: "center", marginBottom, lineHeight: 1.45, fontFamily: F, padding: "0 8px" }}>
          {subtitle}
        </div>
      )}
    </>
  );
}
